import { hasPermission, PERMISSIONS, type Permission } from '@repo/contracts';
import { AppShell as SharedAppShell } from '@repo/ui';
import { BadgeCheck, FileSignature, LayoutDashboard, Users } from 'lucide-react';
import { Outlet } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { systemRoleLabel } from '@/constants/labels';
import { activeAppRoleOf } from '@/hooks/auth/app-roles';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { ADMIN_NAV_ITEMS } from '@/pages/admin/admin-nav';
import { SwitchRoleMenu } from '@/shared/components/switch-role/SwitchRoleMenu';
import {
  selectActiveRole,
  selectCurrentUser,
  selectHeldRoles,
  selectPermissions,
} from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

const NAV_ITEMS = [
  {
    to: APP_ROUTES.home,
    label: 'Dashboard',
    icon: LayoutDashboard,
    permission: PERMISSIONS.candidateRead,
    end: true,
  },
  {
    to: APP_ROUTES.candidates.list,
    label: 'Candidates',
    icon: Users,
    permission: PERMISSIONS.candidateRead,
  },
  {
    to: APP_ROUTES.employees.list,
    label: 'Employees',
    icon: BadgeCheck,
    permission: PERMISSIONS.employeeRead,
  },
  {
    to: APP_ROUTES.directNda.list,
    label: 'Direct NDA',
    icon: FileSignature,
    permission: PERMISSIONS.directNdaRead,
  },
  ...ADMIN_NAV_ITEMS,
];

export function AppShell() {
  const user = useAppSelector(selectCurrentUser);
  const effectivePermissions = useAppSelector(selectPermissions);
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);
  const signOut = useSignOut();
  const subtitleRole = activeAppRoleOf(heldRoles, activeRole);

  const canAccess = (permission?: string): boolean =>
    !permission || hasPermission(effectivePermissions, [permission as Permission]);

  return (
    <SharedAppShell
      productName="Cyrino"
      sectionName="Onboarding"
      navItems={NAV_ITEMS}
      canAccess={canAccess}
      fullName={user?.fullName}
      subtitle={subtitleRole ? systemRoleLabel(subtitleRole) : undefined}
      onSignOut={signOut}
      accountMenuExtra={<SwitchRoleMenu />}
    >
      <Outlet />
    </SharedAppShell>
  );
}
